import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import Sidebar from 'components/Sidebar';
import { useUser } from 'utils/userContext';
import { supabase } from 'utils/supabaseClient';

const COLORS = ['#8B5CF6', '#A855F7', '#C084FC', '#DDD6FE', '#F3E8FF', '#E879F9'];

// Custom label for pie charts with better visibility
const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Hide labels for very small slices

    return (
        <text
            x={x}
            y={y}
            fill="white"
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            fontSize="12"
            fontWeight="bold"
            stroke="rgba(0,0,0,0.5)"
            strokeWidth="0.5"
        >
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

// Custom Tooltip Component
const CustomTooltip = ({ content, children }) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div
            className="relative inline-block"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {children}
            {showTooltip && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 z-50">
                    <div className="bg-background-pop text-text p-4 rounded-lg shadow-lg border border-border max-w-lg min-w-80">
                        {content}
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-background-pop"></div>
                    </div>
                </div>
            )}
        </div>
    );
};

function AdminStats() {
    const { user, loading } = useUser();
    const [statsData, setStatsData] = useState(null);
    const [lessonData, setLessonData] = useState([]);
    const [bugsList, setBugsList] = useState([]);
    const [loadingStats, setLoadingStats] = useState(true);
    const [showingBugData, setShowingBugData] = useState(null)

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch lesson structure
                const lessonResponse = await fetch('/lessons.json');
                const lessons = await lessonResponse.json();
                setLessonData(lessons);

                // Fetch user stats
                const { data: userStats, error } = await supabase
                    .from('user_stats')
                    .select('*')
                    .order('timestamp', { ascending: false });

                if (error) throw error;

                // Fetch user names for recent users
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                const recentStats = userStats.filter(stat => new Date(stat.timestamp) >= weekAgo);
                const recentUserIds = [...new Set(recentStats.map(s => s.user_id))];

                let usersData = [];
                if (recentUserIds.length > 0) {
                    const { data: users, error: usersError } = await supabase
                        .from('users')
                        .select('id, username')
                        .in('id', recentUserIds);

                    if (!usersError) {
                        usersData = users || [];
                    }

                }

                // Process the data
                const processed = processStatsData(userStats, lessons, usersData);
                setStatsData(processed);

                const { data: bugs, error: bugsError } = await supabase
                    .from('bug_reports')
                    .select('*')
                    .order('created_at', { ascending: false });

                if (bugsError) throw bugsError;

                setBugsList(bugs || []);
            } catch (error) {
                console.error('Error fetching stats:', error);
            } finally {
                setLoadingStats(false);
            }
        };

        if (!loading && user?.is_admin) {
            fetchData();
        }
    }, [loading, user]);

    const processStatsData = (userStats, lessons, usersData = []) => {
        if (!userStats || !lessons) return null;

        // Overall stats
        const totalAttempts = userStats.length;
        const correctAttempts = userStats.filter(stat => stat.is_correct).length;
        const overallAccuracy = totalAttempts > 0 ? (correctAttempts / totalAttempts * 100).toFixed(1) : 0;
        const totalTimeSpent = userStats.reduce((sum, stat) => sum + stat.time_spent_ms, 0);
        const avgTimePerChallenge = totalAttempts > 0 ? (totalTimeSpent / totalAttempts / 1000).toFixed(1) : 0;
        const helpUsageCount = userStats.filter(stat => stat.generated_help).length;
        const helpUsageRate = totalAttempts > 0 ? (helpUsageCount / totalAttempts * 100).toFixed(1) : 0;

        // Challenge-level stats
        const challengeStats = {};
        userStats.forEach(stat => {
            const key = `U${stat.unit_num}C${stat.concept_num}L${stat.lesson_num}`;
            if (!challengeStats[key]) {
                challengeStats[key] = {
                    attempts: 0,
                    correct: 0,
                    totalTime: 0,
                    helpUsed: 0,
                    unitNum: stat.unit_num,
                    conceptNum: stat.concept_num,
                    lessonNum: stat.lesson_num
                };
            }
            challengeStats[key].attempts++;
            if (stat.is_correct) challengeStats[key].correct++;
            challengeStats[key].totalTime += stat.time_spent_ms;
            if (stat.generated_help) challengeStats[key].helpUsed++;
        });

        const challengeStatsArray = Object.entries(challengeStats).map(([key, stats]) => {
            const accuracy = (stats.correct / stats.attempts * 100).toFixed(1);
            const avgTime = (stats.totalTime / stats.attempts / 1000).toFixed(1);
            const helpRate = (stats.helpUsed / stats.attempts * 100).toFixed(1);

            // Get challenge name from lesson data
            const challengeName = getShortChallengeName(lessons, stats.unitNum, stats.conceptNum, stats.lessonNum);

            return {
                id: key,
                name: challengeName,
                attempts: stats.attempts,
                accuracy: parseFloat(accuracy),
                avgTime: parseFloat(avgTime),
                helpRate: parseFloat(helpRate),
                ...stats
            };
        }).sort((a, b) => {
            // Sort by unit, then concept, then lesson
            if (a.unitNum !== b.unitNum) return a.unitNum - b.unitNum;
            if (a.conceptNum !== b.conceptNum) return a.conceptNum - b.conceptNum;
            return a.lessonNum - b.lessonNum;
        });

        // Unit-level stats
        const unitStats = {};
        userStats.forEach(stat => {
            if (!unitStats[stat.unit_num]) {
                unitStats[stat.unit_num] = { attempts: 0, correct: 0, totalTime: 0, helpUsed: 0 };
            }
            unitStats[stat.unit_num].attempts++;
            if (stat.is_correct) unitStats[stat.unit_num].correct++;
            unitStats[stat.unit_num].totalTime += stat.time_spent_ms;
            if (stat.generated_help) unitStats[stat.unit_num].helpUsed++;
        });

        // Get unique users from past week
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const recentStats = userStats.filter(stat => new Date(stat.timestamp) >= weekAgo);
        const recentUserIds = [...new Set(recentStats.map(s => s.user_id))];

        // Map user IDs to user info
        const recentUsersWithNames = recentUserIds.map(userId => {
            const userInfo = usersData.find(u => u.id === userId);
            return {
                id: userId,
                name: userInfo?.username || 'Unknown User',
            };
        });

        // Concept-level stats instead of unit-level
        const conceptStats = {};
        userStats.forEach(stat => {
            const key = `${stat.unit_num}-${stat.concept_num}`;
            if (!conceptStats[key]) {
                conceptStats[key] = {
                    attempts: 0,
                    correct: 0,
                    totalTime: 0,
                    helpUsed: 0,
                    unitNum: stat.unit_num,
                    conceptNum: stat.concept_num
                };
            }
            conceptStats[key].attempts++;
            if (stat.is_correct) conceptStats[key].correct++;
            conceptStats[key].totalTime += stat.time_spent_ms;
            if (stat.generated_help) conceptStats[key].helpUsed++;
        });

        const conceptStatsArray = Object.entries(conceptStats).map(([key, stats]) => {
            const [unitNum, conceptNum] = key.split('-').map(Number);
            const conceptName = getConceptInfo(lessons, unitNum, conceptNum);
            return {
                concept: conceptName,
                unitNum,
                conceptNum,
                attempts: stats.attempts,
                accuracy: parseFloat((stats.correct / stats.attempts * 100).toFixed(1)),
                avgTime: parseFloat((stats.totalTime / stats.attempts / 1000).toFixed(1)),
                helpRate: parseFloat((stats.helpUsed / stats.attempts * 100).toFixed(1))
            };
        }).sort((a, b) => a.unitNum - b.unitNum || a.conceptNum - b.conceptNum);

        // Time-based stats (daily activity)
        const dailyStats = {};
        userStats.forEach(stat => {
            const date = new Date(stat.timestamp).toLocaleDateString();
            if (!dailyStats[date]) {
                dailyStats[date] = { attempts: 0, correct: 0, uniqueUsers: new Set() };
            }
            dailyStats[date].attempts++;
            if (stat.is_correct) dailyStats[date].correct++;
            dailyStats[date].uniqueUsers.add(stat.user_id);
        });

        const dailyStatsArray = Object.entries(dailyStats)
            .map(([date, stats]) => ({
                date,
                attempts: stats.attempts,
                accuracy: parseFloat((stats.correct / stats.attempts * 100).toFixed(1)),
                uniqueUsers: stats.uniqueUsers.size
            }))
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .slice(-14); // Last 14 days

        // All challenges with accuracy
        const allChallengeStats = challengeStatsArray.map(challenge => ({
            ...challenge,
            shortName: getShortChallengeName(lessons, challenge.unitNum, challenge.conceptNum, challenge.lessonNum)
        })); // Already sorted by unit/concept/lesson order from challengeStatsArray

        // Help usage distribution pie chart
        const helpDistribution = [
            { name: 'No Help Used', value: userStats.filter(s => !s.generated_help).length, color: '#8B5CF6' },
            { name: 'Help Used', value: userStats.filter(s => s.generated_help).length, color: '#C084FC' }
        ];

        // Time distribution chart (new chart)
        const timeRanges = [
            { name: 'Under 10s', min: 0, max: 10000, color: '#8B5CF6' },
            { name: '10-30s', min: 10000, max: 30000, color: '#A855F7' },
            { name: '30s-1m', min: 30000, max: 60000, color: '#C084FC' },
            { name: '1-2m', min: 60000, max: 120000, color: '#DDD6FE' },
            { name: 'Over 2m', min: 120000, max: Infinity, color: '#F3E8FF' }
        ];

        const timeDistribution = timeRanges.map(range => ({
            name: range.name,
            value: userStats.filter(s => s.time_spent_ms >= range.min && s.time_spent_ms < range.max).length,
            color: range.color
        }));

        return {
            overview: {
                totalAttempts,
                overallAccuracy: parseFloat(overallAccuracy),
                avgTimePerChallenge: parseFloat(avgTimePerChallenge),
                helpUsageRate: parseFloat(helpUsageRate),
                recentUsers: recentUsersWithNames.length,
                recentUsersList: recentUsersWithNames
            },
            conceptStats: conceptStatsArray,
            allChallengeStats: allChallengeStats,
            dailyStats: dailyStatsArray,
            helpDistribution: helpDistribution,
            timeDistribution: timeDistribution
        };
    };

    const getConceptInfo = (lessons, unitNum, conceptNum) => {
        try {
            const unit = lessons[unitNum - 1];
            const concept = unit?.concepts[conceptNum - 1];
            return `U${unitNum}: ${concept?.concept || 'Unknown'}`;
        } catch {
            return `U${unitNum}C${conceptNum}`;
        }
    };

    const getShortChallengeName = (lessons, unitNum, conceptNum, lessonNum) => {
        try {
            const unit = lessons[unitNum - 1];
            const concept = unit?.concepts[conceptNum - 1];
            return `U${unitNum}C${conceptNum}-${lessonNum}`;
        } catch {
            return `U${unitNum}C${conceptNum}L${lessonNum}`;
        }
    };

    if (loading || !user?.is_admin) {
        return (
            <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
                <Sidebar />
                <div className="flex-grow h-full bg-background text-text flex items-center justify-center">
                    <h1 className="text-2xl">Access Denied</h1>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
            <style>
                {`
                    .recharts-pie-label-text {
                        fill: white !important;
                        font-weight: bold;
                        text-shadow: 1px 1px 2px rgba(0,0,0,0.7);
                    }
                    .recharts-legend-item-text {
                        color: var(--text) !important;
                    }
                    .recharts-default-legend .recharts-legend-item-text {
                        color: var(--text) !important;
                    }
                `}
            </style>
            <Sidebar />
            <div className="flex-grow h-full bg-background text-text overflow-auto">
                <div className="p-6">
                    <h1 className="text-3xl font-bold mb-6 text-center">Admin Statistics Dashboard</h1>

                    {loadingStats ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-xl">Loading statistics...</div>
                        </div>
                    ) : !statsData ? (
                        <div className="flex items-center justify-center h-64">
                            <div className="text-xl">No data available</div>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {/* Overview Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                <CustomTooltip content={<div>Total number of challenge attempts across all users</div>}>
                                    <div className="bg-background-light p-4 rounded-lg border border-border cursor-pointer">
                                        <h3 className="text-sm font-medium text-text-light">Total Attempts</h3>
                                        <p className="text-2xl font-bold text-purple-500">{statsData.overview.totalAttempts.toLocaleString()}</p>
                                    </div>
                                </CustomTooltip>

                                <CustomTooltip content={<div>Percentage of challenges completed correctly across the platform</div>}>
                                    <div className="bg-background-light p-4 rounded-lg border border-border cursor-pointer">
                                        <h3 className="text-sm font-medium text-text-light">Overall Accuracy</h3>
                                        <p className="text-2xl font-bold text-purple-400">{statsData.overview.overallAccuracy}%</p>
                                    </div>
                                </CustomTooltip>

                                <CustomTooltip content={<div>Average time spent per challenge completion</div>}>
                                    <div className="bg-background-light p-4 rounded-lg border border-border cursor-pointer">
                                        <h3 className="text-sm font-medium text-text-light">Avg Time/Challenge</h3>
                                        <p className="text-2xl font-bold text-purple-300">{statsData.overview.avgTimePerChallenge}s</p>
                                    </div>
                                </CustomTooltip>

                                <CustomTooltip content={<div>Percentage of challenges where users requested AI help</div>}>
                                    <div className="bg-background-light p-4 rounded-lg border border-border cursor-pointer">
                                        <h3 className="text-sm font-medium text-text-light">Help Usage Rate</h3>
                                        <p className="text-2xl font-bold text-purple-200">{statsData.overview.helpUsageRate}%</p>
                                    </div>
                                </CustomTooltip>

                                <CustomTooltip content={
                                    <div>
                                        <div className="font-bold mb-2">Active users in the past week:</div>
                                        <div className="max-h-32 overflow-y-auto">
                                            {statsData.overview.recentUsersList.length > 0 ?
                                                statsData.overview.recentUsersList.map((userInfo, index) => (
                                                    <div key={userInfo.user_id} className="text-sm py-1">
                                                        {userInfo.name}
                                                    </div>
                                                )) :
                                                <div className="text-sm">No recent users</div>
                                            }
                                        </div>
                                    </div>
                                }>
                                    <div className="bg-background-light p-4 rounded-lg border border-border cursor-pointer">
                                        <h3 className="text-sm font-medium text-text-light">Recent Users</h3>
                                        <p className="text-2xl font-bold text-purple-500">{statsData.overview.recentUsers}</p>
                                    </div>
                                </CustomTooltip>
                            </div>

                            {/* Concept Performance */}
                            <div className="bg-background-light p-6 rounded-lg border border-border">
                                <h2 className="text-xl font-bold mb-4">Concept Performance</h2>
                                <ResponsiveContainer width="100%" height={300}>
                                    <BarChart data={statsData.conceptStats}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" />
                                        <XAxis dataKey="concept" angle={-45} textAnchor="end" height={80} fontSize={12} />
                                        <YAxis yAxisId="left" orientation="left" />
                                        <YAxis yAxisId="right" orientation="right" />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: 'var(--background-pop)',
                                                border: '1px solid var(--border)',
                                                borderRadius: '8px',
                                                color: 'var(--text)'
                                            }}
                                        />
                                        <Legend />
                                        <Bar yAxisId="left" dataKey="accuracy" fill="#8B5CF6" name="Accuracy %" />
                                        <Bar yAxisId="right" dataKey="attempts" fill="#C084FC" name="Attempts" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* All Challenges Accuracy */}
                            <div className="bg-background-light p-6 rounded-lg border border-border">
                                <h2 className="text-xl font-bold mb-4">All Challenge Accuracy Rates</h2>
                                <ResponsiveContainer width="100%" height={500}>
                                    <BarChart data={statsData.allChallengeStats} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" />
                                        <XAxis
                                            dataKey="shortName"
                                            angle={-75}
                                            textAnchor="end"
                                            height={80}
                                            fontSize={10}
                                            interval={0}
                                        />
                                        <YAxis domain={[0, 100]} />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: 'var(--background-pop)',
                                                border: '1px solid var(--border)',
                                                borderRadius: '8px',
                                                color: 'var(--text)'
                                            }}
                                            formatter={(value, name) => [`${value}%`, 'Accuracy']}
                                            labelFormatter={(label) => `Challenge: ${label}`}
                                        />
                                        <Bar dataKey="accuracy" fill="#8B5CF6" name="Accuracy %" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Help Usage and Time Distribution */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* Help Usage Distribution */}
                                <div className="bg-background-light p-6 rounded-lg border border-border">
                                    <h2 className="text-xl font-bold mb-4">Help Usage Distribution</h2>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={statsData.helpDistribution}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={renderCustomLabel}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {statsData.helpDistribution.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: 'var(--background-pop)',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: '8px',
                                                    color: 'var(--text)'
                                                }}
                                                formatter={(value, name) => [`${value} attempts`, name]}
                                            />
                                            <Legend
                                                wrapperStyle={{
                                                    color: 'var(--text)'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>

                                {/* Time Distribution */}
                                <div className="bg-background-light p-6 rounded-lg border border-border">
                                    <h2 className="text-xl font-bold mb-4">Time Per Challenge Distribution</h2>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={statsData.timeDistribution}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={false}
                                                label={renderCustomLabel}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                dataKey="value"
                                            >
                                                {statsData.timeDistribution.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                contentStyle={{
                                                    backgroundColor: 'var(--background-pop)',
                                                    border: '1px solid var(--border)',
                                                    borderRadius: '8px',
                                                    color: 'var(--text)'
                                                }}
                                                formatter={(value, name) => [`${value} attempts`, name]}
                                            />
                                            <Legend
                                                wrapperStyle={{
                                                    color: 'var(--text)'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Daily Activity */}
                            <div className="bg-background-light p-6 rounded-lg border border-border">
                                <h2 className="text-xl font-bold mb-4">Daily Activity (Last 14 Days)</h2>
                                <ResponsiveContainer width="100%" height={300}>
                                    <AreaChart data={statsData.dailyStats}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#6B7280" />
                                        <XAxis dataKey="date" />
                                        <YAxis yAxisId="left" orientation="left" />
                                        <YAxis yAxisId="right" orientation="right" />
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: 'var(--background-pop)',
                                                border: '1px solid var(--border)',
                                                borderRadius: '8px',
                                                color: 'var(--text)'
                                            }}
                                        />
                                        <Legend />
                                        <Area yAxisId="left" type="monotone" dataKey="attempts" stackId="1" stroke="#8B5CF6" fill="#8B5CF6" name="Attempts" />
                                        <Area yAxisId="right" type="monotone" dataKey="uniqueUsers" stroke="#C084FC" fill="#C084FC" fillOpacity={0.6} name="Daily Users" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                            {/* Bug Reports */}
                            <div className="bg-background-light p-6 rounded-lg border border-border">
                                <h2 className="text-xl font-bold mb-4">Bug Reports</h2>
                                {bugsList.length === 0 ? (
                                    <div className="text-sm text-text-light">No bug reports submitted yet.</div>
                                ) : (
                                    <table className="min-w-full divide-y divide-border">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-light uppercase tracking-wider">User</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-light uppercase tracking-wider">Report</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-light uppercase tracking-wider">Timestamp</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-text-light uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border">
                                            {/* Sort: unresolved first, then resolved */}
                                            {[...bugsList]
                                                .sort((a, b) => {
                                                    if (a.resolved === b.resolved) return 0;
                                                    return a.resolved ? 1 : -1;
                                                })
                                                .map((bug, index) => (
                                                    <tr key={bug.id} className={`hover:bg-background-hover ${bug.resolved ? 'opacity-60' : ''}`}>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                                                            {bug.username || 'Unknown User'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-wrap text-sm text-text">
                                                            {bug.report}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text">
                                                            {new Date(bug.created_at).toLocaleString()}
                                                        </td>

                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-text gap-2 flex">
                                                            <button
                                                                className="text-blue-500 hover:text-blue-700"
                                                                onClick={() => {
                                                                    setShowingBugData(bug.data ?? {})
                                                                }}
                                                            >
                                                                Data
                                                            </button>
                                                            {bug.resolved ? (
                                                                <span className="flex items-center gap-2">
                                                                    <button
                                                                        className="text-purple-500 hover:text-purple-700"
                                                                        onClick={async () => {
                                                                            const { error } = await supabase
                                                                                .from('bug_reports')
                                                                                .update({ resolved: false })
                                                                                .eq('id', bug.id);
                                                                            if (error) {
                                                                                console.error('Error unresolving bug report:', error);
                                                                            }
                                                                            setBugsList(bugsList.map(b => b.id === bug.id ? { ...b, resolved: false } : b));
                                                                        }}
                                                                    >
                                                                        Unresolve
                                                                    </button>
                                                                    <button
                                                                        className="text-red-500 hover:text-red-700"
                                                                        onClick={async () => {
                                                                            if (window.confirm('Are you sure you want to permanently delete this bug report?')) {
                                                                                const { error } = await supabase
                                                                                    .from('bug_reports')
                                                                                    .delete()
                                                                                    .eq('id', bug.id);
                                                                                if (error) {
                                                                                    console.error('Error deleting bug report:', error);
                                                                                }
                                                                                setBugsList(bugsList.filter(b => b.id !== bug.id));
                                                                            }
                                                                        }}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </span>
                                                            ) : (
                                                                <button
                                                                    className="text-red-500 hover:text-red-700"
                                                                    onClick={async () => {
                                                                        const { error } = await supabase
                                                                            .from('bug_reports')
                                                                            .update({ resolved: true })
                                                                            .eq('id', bug.id);
                                                                        if (error) {
                                                                            console.error('Error resolving bug report:', error);
                                                                        }
                                                                        setBugsList(bugsList.map(b => b.id === bug.id ? { ...b, resolved: true } : b));
                                                                    }}
                                                                >
                                                                    Resolve
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {showingBugData && <div className="fixed inset-0  " onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowingBugData(null)
                    }
                }}>
                    <div className="absolute inset-0 bg-slate-800 w-3/4 h-[90vh] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-2xl p-8 text-white">
                        <h2 className="text-2xl font-bold mb-4">Bug Report Details</h2>
                        <pre>{JSON.stringify(deepFormatJSON(showingBugData), null, 2)}</pre>
                    </div>
                </div>}
            </div>
        </div>
    );
}

function deepFormatJSON(obj) {
    if (typeof obj === 'string') {
        try {
            const parsed = JSON.parse(obj);
            return deepFormatJSON(parsed); // parse and recurse
        } catch {
            return obj; // not JSON, leave as is
        }
    } else if (Array.isArray(obj)) {
        return obj.map(deepFormatJSON);
    } else if (typeof obj === 'object' && obj !== null) {
        const newObj = {};
        for (const key in obj) {
            newObj[key] = deepFormatJSON(obj[key]);
        }
        return newObj;
    }
    return obj; // primitive value
}

export default AdminStats;
